import { useQueryClient } from '@tanstack/react-query'
import { CreditCard, Plus } from 'lucide-react'
import { useEffect } from 'react'

import {
  formatCardBrand,
  formatCardNumber,
  type MemberPaymentMethod,
} from '@snootsvet/shared/portal'

import { AddPaymentMethodForm } from '@/customer/components/billing/add-payment-method-form'
import { Badge } from '@/customer/components/ui/badge'
import { Button } from '@/customer/components/ui/button'
import { useCreateSetupIntent } from '@/customer/hooks/use-create-setup-intent'
import { meQueryKey } from '@/customer/hooks/use-get-me'
import { useListPaymentMethods } from '@/customer/hooks/use-list-payment-methods'
import { cn } from '@/customer/lib/utils'

/**
 * Member's saved cards with inline Stripe card entry.
 *
 * Prefers the default card, falling back to the first saved card when the
 * selection is empty. Newly added cards become the selected card.
 */
export function PaymentMethods({
  value,
  onChange,
}: {
  value: string | null
  onChange: (paymentMethodId: string) => void
}) {
  const cards = useListPaymentMethods()
  const setupIntent = useCreateSetupIntent()
  const queryClient = useQueryClient()
  const methods = cards.data?.paymentMethods

  useEffect(() => {
    if (value || !methods?.length) return
    onChange((methods.find((method) => method.isDefault) ?? methods[0]).id)
  }, [methods, value, onChange])

  async function handleCardAdded() {
    setupIntent.reset()
    const previousIds = new Set(methods?.map((method) => method.id) ?? [])
    const refreshed = await cards.refetch()
    // Stripe's card ordering is unspecified, so new cards are identified by ID.
    const added = refreshed.data?.paymentMethods.find((method) => !previousIds.has(method.id))
    if (added) onChange(added.id)
  }

  return (
    <div className="space-y-3">
      {cards.isPending ? (
        <div className="space-y-2" role="status" aria-label="Loading saved cards">
          <div className="h-14 animate-pulse rounded-lg bg-muted" />
          <div className="h-14 animate-pulse rounded-lg bg-muted" />
        </div>
      ) : cards.isError ? (
        <div role="alert" className="flex items-center gap-3 text-label-md text-destructive">
          <p>We couldn&apos;t load your saved cards.</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => cards.refetch()}
            disabled={cards.isFetching}
          >
            Retry
          </Button>
        </div>
      ) : methods?.length ? (
        <ul className="grid grid-cols-1 gap-2">
          {methods.map((method) => (
            <li key={method.id}>
              <PaymentMethodRow
                method={method}
                selected={value === method.id}
                onSelect={() => onChange(method.id)}
              />
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-label-md text-muted-foreground">No cards saved yet.</p>
      )}

      {setupIntent.data ? (
        // The secret keys a fresh Elements instance, whose Stripe account and
        // client secret are fixed at mount time.
        <AddPaymentMethodForm
          key={setupIntent.data.clientSecret}
          setupIntent={setupIntent.data}
          onSuccess={handleCardAdded}
          onCancel={() => setupIntent.reset()}
        />
      ) : (
        <button
          type="button"
          className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-border px-4 py-3 text-sm text-muted-foreground transition-colors hover:bg-muted/50 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={setupIntent.isPending}
          onClick={() =>
            setupIntent.mutate(undefined, {
              onSuccess: () => queryClient.invalidateQueries({ queryKey: meQueryKey }),
            })
          }
        >
          <Plus className="size-4" aria-hidden />
          {setupIntent.isPending ? 'Loading…' : methods?.length ? 'Add another card' : 'Add card'}
        </button>
      )}
      {setupIntent.error && (
        <p role="alert" className="text-label-sm text-destructive">
          {setupIntent.error.message}
        </p>
      )}
    </div>
  )
}

function PaymentMethodRow({
  method,
  selected,
  onSelect,
}: {
  method: MemberPaymentMethod
  selected: boolean
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={cn(
        'flex w-full cursor-pointer items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors',
        selected
          ? 'border-primary bg-primary/5 ring-1 ring-primary'
          : 'border-border hover:bg-muted/50'
      )}
    >
      <CreditCard className="size-5 shrink-0 text-muted-foreground" aria-hidden />
      <div className="flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium">{formatCardBrand(method)}</span>
          <span className="text-sm text-muted-foreground">{formatCardNumber(method)}</span>
          {method.isDefault && (
            <Badge variant="info" size="sm">
              Default
            </Badge>
          )}
        </div>
        <span className="text-xs text-muted-foreground">
          Expires {String(method.expMonth).padStart(2, '0')}/{method.expYear}
        </span>
      </div>
    </button>
  )
}
